import { designTokens } from "@itandi/itandi-bb-ui";
import { Box } from "@itandi/itandi-bb-ui/Box";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { Grid } from "@itandi/itandi-bb-ui/Grid";
import { PaginationWithCounter } from "@itandi/itandi-bb-ui/PaginationWithCounter";
import { Table } from "@itandi/itandi-bb-ui/Table";
import { Tbody } from "@itandi/itandi-bb-ui/Tbody";
import { Thead } from "@itandi/itandi-bb-ui/Thead";
import type { ReactElement } from "react";
import React from "react";
import { DealTableSPHeader } from "~/components/specifics/deals/index/table/DealTableSPHeader";
import { DealTableSPRow } from "~/components/specifics/deals/index/table/DealTableSPRow";
import { useDynamicHeight } from "~/hooks/useDynamicHeight";
import type {
	DealIndexClient,
	DealSearchCondition,
	DealSortKey
} from "~/models/Deal";
import type { ItandiAccount } from "~/models/ItandiAccount";
import { DealTableHeader } from "./DealTableHeader";
import { DealTableRow } from "./DealTableRow";

type Props = {
	deals: DealIndexClient[];
	dealSearchCondition: DealSearchCondition;
	handleChangeCondition: (newCondition: DealSearchCondition) => Promise<void>;
	isSp: boolean;
	session: ItandiAccount;
	statusNames: Record<number, string>;
	total: number;
};

export function DealTable({
	deals,
	session,
	dealSearchCondition,
	handleChangeCondition,
	statusNames,
	total,
	isSp
}: Props) {
	const nowSortKey = dealSearchCondition.sortKey ?? "updated_date";
	const nowSortDirection = dealSearchCondition.sortDirection ?? "desc";
	const sortCondition = React.useCallback(
		(sortKey: DealSortKey): "asc" | "desc" | "none" =>
			sortKey === nowSortKey ? nowSortDirection : "none",
		[nowSortKey, nowSortDirection]
	);
	const [currentPageNum, setCurrentPageNum] = React.useState<number>(
		dealSearchCondition.page ?? 1
	);

	const handleChangePage = async (newPageNumber: number): Promise<void> => {
		const newCondition: DealSearchCondition = {
			...dealSearchCondition,
			page: newPageNumber
		};
		setCurrentPageNum(newPageNumber);
		await handleChangeCondition(newCondition);
	};

	const { ref: tableBoxRef, height: tableHeight } =
		useDynamicHeight<HTMLDivElement>();
	const handleSort =
		(selectedSortKey: DealSortKey) => async (): Promise<void> => {
			const sortDirection: "asc" | "desc" =
				selectedSortKey === nowSortKey && nowSortDirection === "desc"
					? "asc"
					: "desc";
			const newCondition: DealSearchCondition = {
				...dealSearchCondition,
				sortDirection,
				sortKey: selectedSortKey
			};
			await handleChangeCondition(newCondition);
		};
	return (
		<>
			{!isSp && (
				<Grid
					gridTemplateRows="1fr auto"
					maxHeight={tableHeight}
					minWidth="1200px"
					ref={tableBoxRef}
				>
					<Table
						containerStyle={{
							overflow: "scroll",
							whiteSpace: "nowrap"
						}}
						fixedColumnIndexOnLeft={1}
						fixedHeader
					>
						<Thead>
							<DealTableHeader
								handleSort={handleSort}
								sortCondition={sortCondition}
							/>
						</Thead>
						<Tbody>
							{deals.map(
								(deal: DealIndexClient): ReactElement => (
									<DealTableRow
										deal={deal}
										key={deal.id}
										session={session}
										statusNames={statusNames}
									/>
								)
							)}
						</Tbody>
					</Table>
					<Flex
						czz={{
							borderTop: `2px solid ${designTokens["color/border/subtle"]}`
						}}
						justifyContent="center"
						paddingY={8}
					>
						<PaginationWithCounter
							currentPageNum={currentPageNum}
							itemsPerPage={50}
							onPageChange={handleChangePage}
							totalItems={total}
						/>
					</Flex>
				</Grid>
			)}
			{isSp ? (
				<>
					<Table fixedHeader>
						<Thead>
							<DealTableSPHeader
								handleSort={handleSort}
								sortCondition={sortCondition}
							/>
						</Thead>
						<Tbody>
							{deals.map(
								(deal: DealIndexClient): ReactElement => (
									<DealTableSPRow
										deal={deal}
										key={deal.id}
										statusNames={statusNames}
									/>
								)
							)}
						</Tbody>
					</Table>
					<Box
						bottom={0}
						czz={{
							backgroundColor: designTokens["color/primary/3"],
							borderTop: `2px solid ${designTokens["color/border/subtle"]}`
						}}
						position="sticky"
						zIndex={1}
					>
						<Flex justifyContent="center" marginY={10}>
							<PaginationWithCounter
								currentPageNum={currentPageNum}
								itemsPerPage={50}
								onPageChange={handleChangePage}
								totalItems={total}
							/>
						</Flex>
					</Box>
				</>
			) : null}
		</>
	);
}
